import moment, { MomentInput } from "moment";
import _ from "lodash";
import { StorageType } from "@/views/enums/storageType";
import { AiQuotaType } from "@/views/enums/aiQuotaType";
import * as XLSX from "xlsx";
/**
 * Chuyển đổi GB sang byte
 * @param {number|string} gb - Giá trị GB cần chuyển đổi
 * @param {boolean} [binary=true] - Sử dụng hệ nhị phân (1024) hay thập phân (1000)
 * @returns {number} Giá trị byte tương ứng
 */
export function gbToBytes(gb: number, binary = true) {
  // Chuyển đổi đầu vào thành số
  const gbNumber = Number(gb);

  // Xử lý giá trị không hợp lệ
  if (isNaN(gbNumber) || gbNumber < 0) {
    console.warn('Invalid input for GB conversion:', gb);
    return 0;
  }

  // Tính toán hệ số chuyển đổi
  const factor = binary ? 1024 : 1000;
  return gbNumber * Math.pow(factor, 3);
}
// Chuyển đổi byte từ api sang G để hiển thị trên FE
export function bytesToGB(bytes, binary = true, decimals = 0) {
  const bytesNumber = Number(bytes);

  if (!bytes && bytes !== 0) {
    return 0; // Nếu undefined, null, hoặc rỗng → trả về 0
  }

  if (isNaN(bytesNumber) || bytesNumber < 0) {
    console.warn('Invalid input for bytes conversion:', bytes);
    return 0;
  }

  const factor = binary ? 1024 : 1000;
  const gb = bytesNumber / Math.pow(factor, 3);

  return Math.round(gb * Math.pow(10, decimals)) / Math.pow(10, decimals);
}

export function bytesToMB(bytes, binary = true, decimals = 0) {
  const bytesNumber = Number(bytes);

  if (!bytes && bytes !== 0) {
    return 0; // Nếu undefined, null, hoặc rỗng → trả về 0
  }

  if (isNaN(bytesNumber) || bytesNumber < 0) {
    console.warn('Invalid input for bytes conversion:', bytes);
    return 0;
  }

  const factor = binary ? 1024 : 1000;
  // Thay đổi số mũ từ 3 (GB) thành 2 (MB)
  const mb = bytesNumber / Math.pow(factor, 2);

  return Math.round(mb * Math.pow(10, decimals)) / Math.pow(10, decimals);
}
// If change to GB quá nhỏ sẽ chuyển tành MB
// Cần có 2 hàm này ở ngoài (đảm bảo chúng trả về số - number)
// export function bytesToGB(bytes, binary = true, decimals = 0) { ... }
// export function bytesToMB(bytes, binary = true, decimals = 0) { ... }

export function smartBytesToUnit(bytes, binary = true, decimals = 1) {
  const bytesNumber = Number(bytes);

  // 1. Kiểm tra đầu vào: Nếu là 0 hoặc invalid, trả về chuỗi rỗng ('')
  if (!bytes && bytes !== 0) {
    return '';
  }

  if (isNaN(bytesNumber) || bytesNumber < 0) {
    console.warn('Invalid input for bytes conversion:', bytes);
    return '';
  }

  // Nếu giá trị là 0, trả về chuỗi rỗng (áp dụng cho trường hợp input = 0)
  if (bytesNumber === 0) {
    return '';
  }

  const factor = binary ? 1024 : 1000;
  const GB_THRESHOLD = Math.pow(factor, 3);

  // 2. Thực hiện kiểm tra và chuyển đổi
  if (bytesNumber < GB_THRESHOLD) {
    // Chuyển sang MB (Số mũ 2)
    const mbValue = bytesToMB(bytesNumber, binary, decimals);

    // 3. Kiểm tra kết quả: Nếu giá trị MB quá nhỏ (làm tròn về 0), trả về chuỗi rỗng
    if (mbValue === 0) {
      return '';
    }

    return `${mbValue} MB`;

  } else {
    // Sử dụng GB (Số mũ 3)
    const gbValue = bytesToGB(bytesNumber, binary, decimals);

    // 3. Kiểm tra kết quả: Nếu giá trị GB quá nhỏ (làm tròn về 0), trả về chuỗi rỗng
    if (gbValue === 0) {
      // Trường hợp này hiếm xảy ra nếu bytesNumber >= GB_THRESHOLD,
      // nhưng cần thiết nếu decimals = 0
      return '';
    }

    return `${gbValue} GB`;
  }
}

export const getDefaultDateRange = (): [Date, Date] => {
  const startDate = moment().subtract(30, "days").startOf("day").toDate();
  const endDate = moment().startOf("day").toDate(); // Không nhảy ngày
  return [startDate, endDate];
};


export function convertSecondsToMinutes(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  return minutes;
}

export function formatMinutesToHhMmSS(totalMinutes: number) {
  // Kiểm tra an toàn cho dữ liệu đầu vào (null, undefined hoặc không phải số)
  if (totalMinutes === null || totalMinutes === undefined || isNaN(totalMinutes)) {
    return "00:00:00";
  }

  // Chuyển đổi toàn bộ sang giây và làm tròn để xử lý chính xác kiểu số thực (double)
  const totalSeconds = Math.round(totalMinutes * 60);

  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;

  // 5. Định dạng số để luôn có 2 chữ số (vd: 09 thay vì 9)
  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(mins).padStart(2, '0');
  const formattedSeconds = String(secs).padStart(2, '0');

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}
// Covert Date from ISO String
// file: util.ts (Phiên bản đã cải tiến)

export function convertDate(isoDateString: string | null): string {
  if (!isoDateString) {
    return '';
  }

  // Moment.js Deprecation Warning KHÔNG ÁP DỤNG ở đây, nhưng
  // bạn cần đảm bảo chuỗi là ISO format hợp lệ để JS Date() hoạt động đúng.
  const date = new Date(isoDateString);

  // Sử dụng các phương thức UTC để tránh Múi giờ địa phương làm sai lệch ngày
  const day = date.getUTCDate();
  const month = date.getUTCMonth() + 1; // Tháng bắt đầu từ 0
  const year = date.getUTCFullYear();

  // Định dạng (Thêm số 0)
  const formattedDay = day < 10 ? '0' + day : day;
  const formattedMonth = month < 10 ? '0' + month : month;

  return `${formattedDay}/${formattedMonth}/${year}`;
}

/**
 * Xử lý phạm vi ngày tháng bằng cách điều chỉnh múi giờ và thêm ngày.
 * Hàm này dùng để tái tạo logic đã được mô tả:
 * - Đặt giờ về 00:00:00.000 UTC.
 * - Thêm 1 ngày vào ngày bắt đầu (startDate + 1).
 * - Thêm 2 ngày vào ngày kết thúc (endDate + 2).
 *
 * @param start - Giá trị ngày bắt đầu (chuỗi ngày hoặc đối tượng Date).
 * @param end - Giá trị ngày kết thúc (chuỗi ngày hoặc đối tượng Date).
 * @returns Một đối tượng chứa fromDate và toDate ở định dạng ISO 8601.
 */
// file: src/utils/dateUtils.ts

/**
 * Xử lý phạm vi ngày tháng bằng cách điều chỉnh múi giờ và thêm ngày.
 *
 * @param startValue Giá trị ngày bắt đầu (ví dụ: this.chartFilterStartDate).
 * @param endValue Giá trị ngày kết thúc (ví dụ: this.chartFilterEndDate).
 * @returns Một đối tượng chứa fromDate và toDate ở định dạng ISO 8601.
 */
export function processDateRange(
  startValue?: string | Date | null,
  endValue?: string | Date | null
): { fromDate: string | null; toDate: string | null } {

  if (!startValue || !endValue) {
    return { fromDate: null, toDate: null };
  }

  // 1. Khởi tạo đối tượng Date
  const startDate = new Date(startValue);
  const endDate = new Date(endValue);

  // Kiểm tra hợp lệ (tránh Invalid Date)
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return { fromDate: null, toDate: null };
  }

  // 2. Đặt giờ về 00:00:00 UTC
  startDate.setUTCHours(0, 0, 0, 0);
  endDate.setUTCHours(0, 0, 0, 0);

  // 3. Điều chỉnh ngày (nếu bạn cần giữ logic +1/+2)
  startDate.setDate(startDate.getDate() + 1);
  endDate.setDate(endDate.getDate() + 2);

  // 4. Trả về ISO string
  return {
    fromDate: startDate.toISOString(),
    toDate: endDate.toISOString(),
  };
}

export function randomStr(length: number): string {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

  let randomStr = "";
  for (let i = 0; i < length; i++) {
    randomStr += _.sample(chars);
  }

  return randomStr;
}

const charactersToCheck = [" ", "-", "+", "?", ""];
export function isValidUserName(strUsername: string) {
  if (!strUsername.match(/^[a-z][a-z0-9._]*$/i)) {
    return false;
  } else {
    return true;
  }
}
export function randomPassword(length: number): string {
  if (length < 3) {
    throw new Error("Password length must be at least 3 to include required character types.");
  }

  const upperCase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowerCase = "abcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const specialChars = "!@#%(";
  const allChars = upperCase + lowerCase + digits + specialChars;


  // Đảm bảo ít nhất 1 ký tự mỗi loại
  const requiredChars = [_.sample(upperCase)!, _.sample(digits)!, _.sample(specialChars)!, _.sample(lowerCase)!];

  // Sinh các ký tự còn lại
  for (let i = requiredChars.length; i < length - 1; i++) {
    requiredChars.push(_.sample(allChars)!);
  }

  // Trộn ngẫu nhiên để không bị đoán vị trí
  /// Bắt đầu với 1 loại ngẫu nhiên ký tự hoặc số (tránh ký tự đặc biệt ở đầu tiên) + mẫu random `requiredChars`
  return randomStr(1) + _.shuffle(requiredChars).join("");
}

export function localDateTimeFormat(value: MomentInput): string {
  const m = moment(value);
  if (m.isValid()) {
    return m.format("HH:mm DD/MM/YYYY");
  }
}

export function formatWbDateTime(value: MomentInput): string {
  const m = moment(value);
  if (m.isValid()) {
    return m.format("DD/MM/YYYY HH:mm:ss");
  }
  return "";
}

export function localDateFormat(value: MomentInput): string {
  try {
    const m = moment(value);
    if (m.isValid()) {
      return m.format("DD/MM/YYYY");
    }
  } catch (error) {
    return '';
  }
}

export function localTimeFormat(value: MomentInput): string {
  const m = moment(value);
  if (m.isValid()) {
    return m.format("HH:mm");
  }
}

export function delay(timeout: number): Promise<any> {
  return new Promise((resolve) => setTimeout(resolve, timeout));
}

export function joinStrings(a: string[], separator: string) {
  return a
    .map((t) => t?.trim())
    .filter((t) => t)
    .join(separator);
}

export function countPredicate(arr: any[], predicate: Function = _.identity): number {
  let count = 0;
  for (const item of arr) {
    if (predicate(item)) {
      count++;
    }
  }
  return count;
}

export function deleteProps(obj: any, predicate: (value: any) => boolean) {
  for (const key in obj) {
    if (obj.hasOwnProperty(key) && predicate(obj[key])) {
      delete obj[key];
    }
  }
  return obj;
}

export function deleteNilProps(obj: any) {
  return deleteProps(obj, _.isNil);
}

export function deleteUndefinedProps(obj: any) {
  return deleteProps(obj, _.isUndefined);
}

export function deleteFalsyProps(obj: any) {
  return deleteProps(obj, (value) => !value);
}

export function jsonClone(obj: any) {
  if (!obj) return obj;
  return JSON.parse(JSON.stringify(obj));
}

export function removeAccents(str: string): string {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D");
}

const controlCharsRegEx =
  /[\0-\x1F\x7F-\x9F\xAD\u0378\u0379\u037F-\u0383\u038B\u038D\u03A2\u0528-\u0530\u0557\u0558\u0560\u0588\u058B-\u058E\u0590\u05C8-\u05CF\u05EB-\u05EF\u05F5-\u0605\u061C\u061D\u06DD\u070E\u070F\u074B\u074C\u07B2-\u07BF\u07FB-\u07FF\u082E\u082F\u083F\u085C\u085D\u085F-\u089F\u08A1\u08AD-\u08E3\u08FF\u0978\u0980\u0984\u098D\u098E\u0991\u0992\u09A9\u09B1\u09B3-\u09B5\u09BA\u09BB\u09C5\u09C6\u09C9\u09CA\u09CF-\u09D6\u09D8-\u09DB\u09DE\u09E4\u09E5\u09FC-\u0A00\u0A04\u0A0B-\u0A0E\u0A11\u0A12\u0A29\u0A31\u0A34\u0A37\u0A3A\u0A3B\u0A3D\u0A43-\u0A46\u0A49\u0A4A\u0A4E-\u0A50\u0A52-\u0A58\u0A5D\u0A5F-\u0A65\u0A76-\u0A80\u0A84\u0A8E\u0A92\u0AA9\u0AB1\u0AB4\u0ABA\u0ABB\u0AC6\u0ACA\u0ACE\u0ACF\u0AD1-\u0ADF\u0AE4\u0AE5\u0AF2-\u0B00\u0B04\u0B0D\u0B0E\u0B11\u0B12\u0B29\u0B31\u0B34\u0B3A\u0B3B\u0B45\u0B46\u0B49\u0B4A\u0B4E-\u0B55\u0B58-\u0B5B\u0B5E\u0B64\u0B65\u0B78-\u0B81\u0B84\u0B8B-\u0B8D\u0B91\u0B96-\u0B98\u0B9B\u0B9D\u0BA0-\u0BA2\u0BA5-\u0BA7\u0BAB-\u0BAD\u0BBA-\u0BBD\u0BC3-\u0BC5\u0BC9\u0BCE\u0BCF\u0BD1-\u0BD6\u0BD8-\u0BE5\u0BFB-\u0C00\u0C04\u0C0D\u0C11\u0C29\u0C34\u0C3A-\u0C3C\u0C45\u0C49\u0C4E-\u0C54\u0C57\u0C5A-\u0C5F\u0C64\u0C65\u0C70-\u0C77\u0C80\u0C81\u0C84\u0C8D\u0C91\u0CA9\u0CB4\u0CBA\u0CBB\u0CC5\u0CC9\u0CCE-\u0CD4\u0CD7-\u0CDD\u0CDF\u0CE4\u0CE5\u0CF0\u0CF3-\u0D01\u0D04\u0D0D\u0D11\u0D3B\u0D3C\u0D45\u0D49\u0D4F-\u0D56\u0D58-\u0D5F\u0D64\u0D65\u0D76-\u0D78\u0D80\u0D81\u0D84\u0D97-\u0D99\u0DB2\u0DBC\u0DBE\u0DBF\u0DC7-\u0DC9\u0DCB-\u0DCE\u0DD5\u0DD7\u0DE0-\u0DF1\u0DF5-\u0E00\u0E3B-\u0E3E\u0E5C-\u0E80\u0E83\u0E85\u0E86\u0E89\u0E8B\u0E8C\u0E8E-\u0E93\u0E98\u0EA0\u0EA4\u0EA6\u0EA8\u0EA9\u0EAC\u0EBA\u0EBE\u0EBF\u0EC5\u0EC7\u0ECE\u0ECF\u0EDA\u0EDB\u0EE0-\u0EFF\u0F48\u0F6D-\u0F70\u0F98\u0FBD\u0FCD\u0FDB-\u0FFF\u10C6\u10C8-\u10CC\u10CE\u10CF\u1249\u124E\u124F\u1257\u1259\u125E\u125F\u1289\u128E\u128F\u12B1\u12B6\u12B7\u12BF\u12C1\u12C6\u12C7\u12D7\u1311\u1316\u1317\u135B\u135C\u137D-\u137F\u139A-\u139F\u13F5-\u13FF\u169D-\u169F\u16F1-\u16FF\u170D\u1715-\u171F\u1737-\u173F\u1754-\u175F\u176D\u1771\u1774-\u177F\u17DE\u17DF\u17EA-\u17EF\u17FA-\u17FF\u180F\u181A-\u181F\u1878-\u187F\u18AB-\u18AF\u18F6-\u18FF\u191D-\u191F\u192C-\u192F\u193C-\u193F\u1941-\u1943\u196E\u196F\u1975-\u197F\u19AC-\u19AF\u19CA-\u19CF\u19DB-\u19DD\u1A1C\u1A1D\u1A5F\u1A7D\u1A7E\u1A8A-\u1A8F\u1A9A-\u1A9F\u1AAE-\u1AFF\u1B4C-\u1B4F\u1B7D-\u1B7F\u1BF4-\u1BFB\u1C38-\u1C3A\u1C4A-\u1C4C\u1C80-\u1CBF\u1CC8-\u1CCF\u1CF7-\u1CFF\u1DE7-\u1DFB\u1F16\u1F17\u1F1E\u1F1F\u1F46\u1F47\u1F4E\u1F4F\u1F58\u1F5A\u1F5C\u1F5E\u1F7E\u1F7F\u1FB5\u1FC5\u1FD4\u1FD5\u1FDC\u1FF0\u1FF1\u1FF5\u1FFF\u200B-\u200F\u202A-\u202E\u2060-\u206F\u2072\u2073\u208F\u209D-\u209F\u20BB-\u20CF\u20F1-\u20FF\u218A-\u218F\u23F4-\u23FF\u2427-\u243F\u244B-\u245F\u2700\u2B4D-\u2B4F\u2B5A-\u2BFF\u2C2F\u2C5F\u2CF4-\u2CF8\u2D26\u2D28-\u2D2C\u2D2E\u2D2F\u2D68-\u2D6E\u2D71-\u2D7E\u2D97-\u2D9F\u2DA7\u2DAF\u2DB7\u2DBF\u2DC7\u2DCF\u2DD7\u2DDF\u2E3C-\u2E7F\u2E9A\u2EF4-\u2EFF\u2FD6-\u2FEF\u2FFC-\u2FFF\u3040\u3097\u3098\u3100-\u3104\u312E-\u3130\u318F\u31BB-\u31BF\u31E4-\u31EF\u321F\u32FF\u4DB6-\u4DBF\u9FCD-\u9FFF\uA48D-\uA48F\uA4C7-\uA4CF\uA62C-\uA63F\uA698-\uA69E\uA6F8-\uA6FF\uA78F\uA794-\uA79F\uA7AB-\uA7F7\uA82C-\uA82F\uA83A-\uA83F\uA878-\uA87F\uA8C5-\uA8CD\uA8DA-\uA8DF\uA8FC-\uA8FF\uA954-\uA95E\uA97D-\uA97F\uA9CE\uA9DA-\uA9DD\uA9E0-\uA9FF\uAA37-\uAA3F\uAA4E\uAA4F\uAA5A\uAA5B\uAA7C-\uAA7F\uAAC3-\uAADA\uAAF7-\uAB00\uAB07\uAB08\uAB0F\uAB10\uAB17-\uAB1F\uAB27\uAB2F-\uABBF\uABEE\uABEF\uABFA-\uABFF\uD7A4-\uD7AF\uD7C7-\uD7CA\uD7FC-\uF8FF\uFA6E\uFA6F\uFADA-\uFAFF\uFB07-\uFB12\uFB18-\uFB1C\uFB37\uFB3D\uFB3F\uFB42\uFB45\uFBC2-\uFBD2\uFD40-\uFD4F\uFD90\uFD91\uFDC8-\uFDEF\uFDFE\uFDFF\uFE1A-\uFE1F\uFE27-\uFE2F\uFE53\uFE67\uFE6C-\uFE6F\uFE75\uFEFD-\uFF00\uFFBF-\uFFC1\uFFC8\uFFC9\uFFD0\uFFD1\uFFD8\uFFD9\uFFDD-\uFFDF\uFFE7\uFFEF-\uFFFB\uFFFE\uFFFF]/g;

export function removeControlChars(str: string): string {
  return str.replace(controlCharsRegEx, "");
}

export function sanitizeSearchText(str: string): string {
  // trim, remove control chars, normalize NFKD, remove accents (vietnamese)
  return removeAccents(str.trim().replace(controlCharsRegEx, "").normalize("NFKD"));
}

const currencyNumberFormat = new Intl.NumberFormat("vi-VN");
export function formatCurrencyAmount(value: number): string {
  return currencyNumberFormat.format(value);
}

// HTMLInputElement doesn't emit "input" event when setting the value programmatically
// so we need to call setCustomValidity on our own
export function clearCustomValidity(classNames = "custom-validity-fix") {
  const inputElements = document.getElementsByClassName(classNames);
  for (const e of inputElements) {
    (e as HTMLInputElement).setCustomValidity("");
  }
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.toString());
    reader.onerror = reject;
  });
}

export function loadCkeditor() {
  if (window["ClassicEditor"]) return window["ClassicEditor"];

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.onload = () => {
      resolve(window["ClassicEditor"]);
    };
    script.onerror = reject;
    script.src = "js/ckeditor.js";

    document.head.appendChild(script);
  });
}

export async function grecaptchaExecute(action = "submit") {
  return new Promise((resolve, reject) =>
    grecaptcha.ready(() =>
      grecaptcha.execute("6Lf026YhAAAAAFgYkAi81jKpLvixk4DcK5g6n5r_", { action }).then(resolve, reject)
    )
  );
}

/// Hàm đặt tên file
export function convertMeetingName2FileName(name: string): string {
  return name
    .replace(/Đ/g, "D") // Chuyển Đ → D
    .replace(/đ/g, "d") // Chuyển đ → d
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s]/g, "")
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("_");
}

export function getCoreDomain(domain?: string): string {
  if (!domain || domain.trim() === '') {
    return null;
  }
  return domain.toLowerCase().replace('.vnpt.vn', '').replace('.vnptit.vn', '');
}

export function vibrantColors() {
  return [
    "#1A2526",
    "#2E3A59",
    "#D32F2F",
    "#F57C00",
    "#388E3C",
    "#7B1FA2",
    "#C2185B",
    "#0288D1",
    "#FBC02D",
    "#212121",
    "#455A64",
    "#8D6E63",
    "#1976D2",
    "#E64A19",
    "#5D4037"
  ];
}

export function stringToHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // 32bit
  }
  return Math.abs(hash);
}

// export function hexToRgba(hex, opacity) {
//   const bigint = parseInt(hex.slice(1), 16);
//   const r = (bigint >> 16) & 255;
//   const g = (bigint >> 8) & 255;
//   const b = bigint & 255;
//   return `rgba(${r}, ${g}, ${b}, ${opacity})`;
// }

export function hexToRgba(text: string, opacity: number = 1) {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = Math.abs(hash) % 360;

  const saturation = 65;
  const lightness = 35;

  // Chuyển từ HSL → RGB
  const h = hue / 360;
  const s = saturation / 100;
  const l = lightness / 100;

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  const toRGB = (t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const r = Math.round(toRGB(h + 1 / 3) * 255);
  const g = Math.round(toRGB(h) * 255);
  const b = Math.round(toRGB(h - 1 / 3) * 255);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export function getColorByText(text: string) {
  const colors = vibrantColors();
  const index = stringToHash(text) % colors.length;
  const hex = colors[index];
  return hex;
}

export function sanitizeObject<T extends Record<string, any>>(obj: T): T {
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [
      k,
      typeof v === "string" ? v.trim() : v,
    ]),
  ) as T;
}

///Hàm chuyển đổi khoảng thời gian dạng hh:mm:ss (VD: "from": 1748588107355, "to": 1748588460004,)
export function convertTimeRange(from: number, to: number): string {
  const formatTime = (timestamp: number): string => {
    const date = new Date(timestamp);
    const hh = date.getHours().toString().padStart(2, '0');
    const mm = date.getMinutes().toString().padStart(2, '0');
    const ss = date.getSeconds().toString().padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  };
  return `${formatTime(from)} - ${formatTime(to)}`;
}

/// Hàm lấy mã màu cho trạng thái dung lượng lưu trữ
export function getStorageStatusColor(used: number, max: number): string {
  /// Ngưỡng so sánh màu
  const threshold = 80;
  const percent = used * 100 / max;
  return percent >= threshold ? '#dc3545' : '#41D6BB';
}

/// Giá trị mặc định cho thời gian lưu trữ các file trong hệ thống (ngày)
export function getDefaultStorageRetention(): number {
  return 90;
}

/// Print log
export function printLog(tag, d) {
  if (window.location.hostname !== 'localhost') return;

  let output;
  try {
    output = JSON.parse(JSON.stringify(d));
  } catch (err) {
    output = d;
  }
  console.log(`===${tag}`, JSON.parse(JSON.stringify(d)));
}

/**
 * Khu vực định dạng các tóm tắt
 */
export function escapeHtml(text = "") {
  return text.replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[c])
  );
}
/**
 * Định dạng nội dung hiển thị cuộc họp
 * @param {string} content - Nội dung gốc (raw text)
 * @param {boolean} withBold - Có bôi đậm header hay không
 * @returns {string} HTML đã format
 */
export function convertTitleContentMeeting(content = "", withBold = true) {
  ///pattern đang định dạng 4 phần: ngày, giờ, người gửi, nội dung
  ///Ví dụ: 25-09-2023 14:30:15 Nguyễn Văn A: Xin chào mọi người
  const linePattern = /^(\d{2}-\d{2}-\d{4}) (\d{2}:\d{2}:\d{2}) ([^(]+)(?:\([^)]*\))?: (.+)$/;

  return (content || "")
    .split(/\r?\n/) // tách từng dòng log
    .map(line => {
      if (!line.trim()) return ""; // bỏ các dòng trống

      const match = line.match(linePattern);
      if (!match) return `<div>${escapeHtml(line)}</div>`;  // Nếu dòng không match regex, hiển thị nguyên văn

      /// Escape HTML từng phần
      const [, date, time, sender, message] = match;
      const datePart = escapeHtml(date);
      const timePart = escapeHtml(time);
      const senderPart = `${escapeHtml(sender.trim())}`;
      let messagePart = markdownToHtml(message);
      // Loại bỏ thẻ <p> ngoài cùng nếu có để tránh giật dòng/khoảng trắng
      if (messagePart.startsWith("<p ") && messagePart.endsWith("</p>")) {
        messagePart = messagePart.substring(messagePart.indexOf(">") + 1, messagePart.length - 4);
      } else if (messagePart.startsWith("<p>") && messagePart.endsWith("</p>")) {
        messagePart = messagePart.substring(3, messagePart.length - 4);
      }

      // Hiển thị dòng đã format theo định dạng: giờ, người gửi, nội dung
      return withBold
        ? `<div style="margin-bottom: 8px;"><b>${timePart}: ${senderPart}</b><br>${messagePart}</div>`
        : `<div style="margin-bottom: 8px;"><b>${timePart}: ${senderPart}</b><br>${messagePart}</div>`;
    })
    .join("");
}
/**
 * Loại tóm tắt hiển thị trong tab tóm tắt chung
 */
export const SUMMARY_TYPE_LABELS = [
  "A. Nội dung tổng hợp từ transcript (STT)",
  "B. Nội dung tổng hợp từ dòng thời gian (Timeline)",
];

/**
 * Hàm định dạng nội dung trong file tóm tắt chung
 * @param content
 * @param withBold
 * @returns
 */
export function convertTitleSummaryMeeting(content: string, withBold: boolean) {
  const txtTitle = "Tiêu Đề";
  const txtDesc = "Mô Tả";
  const txtAction = "Hành Động";
  const txtSummary = "Tóm Tắt Cuộc Họp";
  return content
    .trim()
    .replace(/^Title$/im, withBold ? `<strong>${txtTitle}</strong>` : txtTitle)
    .replace(/^Description$/im, withBold ? `<strong>${txtDesc}</strong>` : txtDesc)
    .replace(/^Action items$/im, withBold ? `<strong>${txtAction}</strong>` : txtAction)
    .replace(/^Meeting summary$/im, withBold ? `<strong>${txtSummary}</strong>` : txtSummary);
}
function isMarkdown(md: string): boolean {
  if (!md) return false;
  // Check for headings at the start of a line
  if (/(^|\n)#{1,6}\s+\S+/.test(md)) return true;
  // Check for list items
  if (/(^|\n)\s*([\*\-\+]|•)\s+\S+/.test(md)) return true;
  if (/(^|\n)\s*\d+\.\s+\S+/.test(md)) return true;
  // Check for code blocks
  if (/(^|\n)\s*```/.test(md)) return true;
  // Check for tables
  if (/(^|\n)\s*\|/.test(md)) return true;
  // Check for blockquotes
  if (/(^|\n)\s*>\s*\S+/.test(md)) return true;
  // Check for horizontal rules
  if (/(^|\n)\s*(\-{3,}|\*{3,}|_{3,})\s*(\n|$)/.test(md)) return true;
  // Check for bold/italic with matching signs containing text
  if (/\*\*[^*` \n]+\*\*/.test(md)) return true;
  if (/__[^_` \n]+__/.test(md)) return true;
  if (/\*(?!\s)[^*` \n]+\*(?!\*)/.test(md)) return true;
  if (/_(?!\s)[^_` \n]+_(?!_)/.test(md)) return true;
  // Check for inline code
  if (/`[^`\n]+`/.test(md)) return true;
  // Check for links or images
  if (/!?\[[^\]\n]+\]\([^)\n]+\)/.test(md)) return true;

  return false;
}

function sanitizeUrl(url: string): string {
  const trimmed = url.trim();
  if (/^(javascript|data|vbscript):/i.test(trimmed)) {
    return "#";
  }
  return trimmed;
}

function renderTableHtml(rows: string[][]): string {
  if (rows.length === 0) return "";
  let html = `<table border="1" cellspacing="0" cellpadding="8" style="width: 100%; border-collapse: collapse; margin-top: 8px; margin-bottom: 16px; border: 1px solid #cbd5e1;">`;
  html += `<thead style="background-color: #f8fafc; font-weight: bold;"><tr>`;
  for (const cell of rows[0]) {
    html += `<th style="padding: 8px 12px; border: 1px solid #cbd5e1; text-align: left; background-color: #f8fafc;">${cell}</th>`;
  }
  html += `</tr></thead>`;
  html += `<tbody>`;
  for (let i = 1; i < rows.length; i++) {
    html += `<tr>`;
    for (const cell of rows[i]) {
      html += `<td style="padding: 8px 12px; border: 1px solid #cbd5e1;">${cell}</td>`;
    }
    html += `</tr>`;
  }
  html += `</tbody></table>`;
  return html;
}

export function markdownToHtml(md: string): string {
  if (!md) return "";

  // Normalize newlines
  let html = md.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  // Escape HTML tags to prevent XSS but allow markdown rendering
  html = html
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // If it is plain text (not markdown), parse safely and return directly
  if (!isMarkdown(md)) {
    const lines = html.split("\n");
    const processedLines: string[] = [];
    let inParagraph = false;
    let currentParagraphLines: string[] = [];
    let prevLineEmpty = false;

    const closeParagraph = () => {
      if (inParagraph) {
        processedLines.push(`<p style="margin-bottom: 0.5rem; white-space: pre-wrap; word-break: break-word;">${currentParagraphLines.join("\n")}</p>`);
        currentParagraphLines = [];
        inParagraph = false;
      }
    };

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed === "") {
        closeParagraph();
        if (prevLineEmpty) {
          processedLines.push('<div style="height: 0.8em;"></div>');
        }
        prevLineEmpty = true;
      } else {
        inParagraph = true;
        currentParagraphLines.push(line);
        prevLineEmpty = false;
      }
    }
    closeParagraph();
    return processedLines.join("\n");
  }

  // Inline styling (only for Markdown content):
  // Images: ![alt](url)
  html = html.replace(/!\[(.*?)\]\((.*?)\)/g, (match, alt, url) => {
    return `<img src="${sanitizeUrl(url)}" alt="${alt}" style="max-width:100%; height:auto;" />`;
  });

  // Links: [text](url)
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, (match, text, url) => {
    return `<a href="${sanitizeUrl(url)}" target="_blank" style="color: var(--primary, #2563eb); text-decoration: underline;">${text}</a>`;
  });

  // Bold (double stars or double underscores)
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/__(.*?)__/g, "<strong>$1</strong>");

  // Italic (single star or single underscore)
  html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");
  html = html.replace(/_(.*?)_/g, "<em>$1</em>");

  // Strikethrough
  html = html.replace(/~~(.*?)~~/g, "<del>$1</del>");

  // Inline code block
  html = html.replace(/`(.*?)`/g, '<code style="font-family: monospace; background-color: #f1f5f9; padding: 2px 4px; border-radius: 4px; font-size: 90%; color: #e11d48;">$1</code>');

  // Block elements:
  const lines = html.split("\n");
  const processedLines: string[] = [];
  let inUl = false;
  let inOl = false;
  let inTable = false;
  let inCodeBlock = false;
  let inBlockquote = false;
  let inParagraph = false;
  let prevLineEmpty = false;
  let currentParagraphLines: string[] = [];
  let tableRows: string[][] = [];
  let codeBlockLines: string[] = [];

  function closeParagraph() {
    if (inParagraph) {
      processedLines.push(`<p style="margin-bottom: 0.5rem; white-space: pre-wrap; word-break: break-word;">${currentParagraphLines.join("\n")}</p>`);
      currentParagraphLines = [];
      inParagraph = false;
    }
  }

  for (const line of lines) {
    const trimmed = line.trim();

    // Code blocks (fenced code blocks)
    if (trimmed.startsWith("```")) {
      closeParagraph();
      prevLineEmpty = false;
      if (inUl) { processedLines.push("</ul>"); inUl = false; }
      if (inOl) { processedLines.push("</ol>"); inOl = false; }
      if (inTable) { processedLines.push(renderTableHtml(tableRows)); inTable = false; }
      if (inBlockquote) { processedLines.push("</div>"); inBlockquote = false; }

      if (inCodeBlock) {
        // Close code block
        processedLines.push(`<pre style="background-color: #0f172a; color: #f8fafc; padding: 12px 16px; border-radius: 6px; overflow-x: auto; font-family: monospace; line-height: 1.5; margin-bottom: 16px;"><code style="color: inherit; background-color: transparent; padding: 0;">${codeBlockLines.join("\n")}</code></pre>`);
        codeBlockLines = [];
        inCodeBlock = false;
      } else {
        // Open code block
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlockLines.push(line);
      continue;
    }

    // Table parsing
    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      closeParagraph();
      prevLineEmpty = false;
      if (inUl) { processedLines.push("</ul>"); inUl = false; }
      if (inOl) { processedLines.push("</ol>"); inOl = false; }
      if (inBlockquote) { processedLines.push("</div>"); inBlockquote = false; }

      const cells = trimmed.split("|").map(c => c.trim()).slice(1, -1);

      // Skip separator row like |---|---|
      if (cells.every(c => /^:?-+:?$/.test(c))) {
        continue;
      }

      if (!inTable) {
        inTable = true;
        tableRows = [];
      }
      tableRows.push(cells);
      continue;
    } else {
      if (inTable) {
        processedLines.push(renderTableHtml(tableRows));
        inTable = false;
      }
    }

    // Horizontal Rules
    if (trimmed === "---" || trimmed === "***" || trimmed === "___") {
      closeParagraph();
      prevLineEmpty = false;
      if (inUl) { processedLines.push("</ul>"); inUl = false; }
      if (inOl) { processedLines.push("</ol>"); inOl = false; }
      if (inBlockquote) { processedLines.push("</div>"); inBlockquote = false; }
      processedLines.push("<hr style='border: none; border-top: 1px solid #cbd5e1; margin: 16px 0;'/>");
      continue;
    }

    // Blockquotes
    if (trimmed.startsWith(">")) {
      closeParagraph();
      prevLineEmpty = false;
      if (inUl) { processedLines.push("</ul>"); inUl = false; }
      if (inOl) { processedLines.push("</ol>"); inOl = false; }

      const content = trimmed.substring(1).trim();
      if (!inBlockquote) {
        processedLines.push("<div style='border-left: 4px solid #3b82f6; padding-left: 16px; margin: 12px 0; color: #475569; font-style: italic;'>");
        inBlockquote = true;
      }
      processedLines.push(`<div>${content}</div>`);
      continue;
    } else {
      if (inBlockquote) {
        processedLines.push("</div>");
        inBlockquote = false;
      }
    }

    // Headers
    if (trimmed.startsWith("#### ")) {
      closeParagraph();
      prevLineEmpty = false;
      if (inUl) { processedLines.push("</ul>"); inUl = false; }
      if (inOl) { processedLines.push("</ol>"); inOl = false; }
      processedLines.push(`<h4>${trimmed.substring(5)}</h4>`);
      continue;
    }
    if (trimmed.startsWith("### ")) {
      closeParagraph();
      prevLineEmpty = false;
      if (inUl) { processedLines.push("</ul>"); inUl = false; }
      if (inOl) { processedLines.push("</ol>"); inOl = false; }
      processedLines.push(`<h3>${trimmed.substring(4)}</h3>`);
      continue;
    }
    if (trimmed.startsWith("## ")) {
      closeParagraph();
      prevLineEmpty = false;
      if (inUl) { processedLines.push("</ul>"); inUl = false; }
      if (inOl) { processedLines.push("</ol>"); inOl = false; }
      processedLines.push(`<h2>${trimmed.substring(3)}</h2>`);
      continue;
    }
    if (trimmed.startsWith("# ")) {
      closeParagraph();
      prevLineEmpty = false;
      if (inUl) { processedLines.push("</ul>"); inUl = false; }
      if (inOl) { processedLines.push("</ol>"); inOl = false; }
      processedLines.push(`<h1>${trimmed.substring(2)}</h1>`);
      continue;
    }

    // Bulleted lists (starts with *, -, +, or bullet character followed by space)
    const ulMatch = line.match(/^(\s*)([\*\-\+•])\s+(.*)$/);
    if (ulMatch) {
      closeParagraph();
      prevLineEmpty = false;
      if (inOl) {
        processedLines.push("</ol>");
        inOl = false;
      }
      if (!inUl) {
        processedLines.push("<ul>");
        inUl = true;
      }
      processedLines.push(`<li>${ulMatch[3]}</li>`);
      continue;
    }

    // Numbered lists (starts with digit(s) followed by period and space)
    const olMatch = line.match(/^(\s*)(\d+)\.\s+(.*)$/);
    if (olMatch) {
      closeParagraph();
      prevLineEmpty = false;
      if (inUl) {
        processedLines.push("</ul>");
        inUl = false;
      }
      if (!inOl) {
        processedLines.push("<ol>");
        inOl = true;
      }
      processedLines.push(`<li>${olMatch[3]}</li>`);
      continue;
    }

    // Regular line / Paragraph
    if (trimmed === "") {
      closeParagraph();
      if (inUl) {
        processedLines.push("</ul>");
        inUl = false;
      }
      if (inOl) {
        processedLines.push("</ol>");
        inOl = false;
      }
      // Chỉ giữ dòng trống cho text thuần nếu có 2 dòng trống liên tiếp trở lên
      if (!inCodeBlock && !inTable && !inBlockquote && prevLineEmpty) {
        processedLines.push('<div style="height: 0.8em;"></div>');
      }
      prevLineEmpty = true;
    } else {
      if (inUl) {
        processedLines.push("</ul>");
        inUl = false;
      }
      if (inOl) {
        processedLines.push("</ol>");
        inOl = false;
      }
      inParagraph = true;
      currentParagraphLines.push(line);
      prevLineEmpty = false;
    }
  }

  // Close open tags if loop ends
  closeParagraph();
  if (inTable) processedLines.push(renderTableHtml(tableRows));
  if (inCodeBlock) {
    processedLines.push(`<pre style="background-color: #0f172a; color: #f8fafc; padding: 12px 16px; border-radius: 6px; overflow-x: auto; font-family: monospace; line-height: 1.5; margin-bottom: 16px;"><code style="color: inherit; background-color: transparent; padding: 0;">${codeBlockLines.join("\n")}</code></pre>`);
  }
  if (inBlockquote) processedLines.push("</div>");
  if (inUl) processedLines.push("</ul>");
  if (inOl) processedLines.push("</ol>");

  return processedLines.join("\n");
}
/**
 * Định dạng nội dung tóm tắt theo timeline để hiển thị trên giao diện
 * @param {Object} item - Dữ liệu 1 đoạn tóm tắt {from, to, content}
 * @returns {string} HTML đã format
 */
export function formattedSummaryTimeLineMeeting(item) {
  const timeLine = convertTimeRange(item.from, item.to);
  const normalizedContent = markdownToHtml(item.content);

  return `
    <div>
      <strong>${timeLine}</strong><br/>
      ${normalizedContent}
    </div>
  `;
}

/**
 * Tính tổng dung lượng tối đa của khách hàng
 * @param {Object} customerInfo - Thông tin khách hàng
 * @param {number} [customerInfo.maxStorageSize=0] - Dung lượng cơ bản
 * @param {number} [customerInfo.purchasedStorageSize=0] - Dung lượng mua thêm
 * @returns {number} Tổng dung lượng tối đa
 */
export function getMaxStorage(maxSize: number, purchasedSize: number) {
  const totalBytes = maxSize + purchasedSize;
  return totalBytes;
}

/// Kiểm tra input có phải email hay không
export function isValidEmail(email: any): boolean {
  return (
    typeof email === 'string' &&
    email.trim() !== '' &&
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.trim())
  );
}

/**
 * @param features - Mảng các tính năng (mỗi phần tử cần có featureName)
 * @param iconColor - Màu của icon check (mặc định: trắng)
 * @returns Chuỗi HTML để gán vào tooltip (ví dụ v-html hoặc title)
 */
export function generateFeatureTooltip(
  features: { featureName: string }[] | undefined,
  iconColor: string = 'white'
): string {
  if (!features || features.length === 0) return '';

  const checkIcon = `
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="${iconColor}" style="margin-right: 5px;" viewBox="0 0 16 16">
      <path d="M13.854 3.646a.5.5 0 0 1 0 .708L6.707 11.5 2.146 6.854a.5.5 0 1 1 .708-.708L6.707 10.293l6.439-6.439a.5.5 0 0 1 .708 0z"/>
    </svg>`;

  return features
    .map(f => `<div class="tooltip-feature">${checkIcon}${f.featureName}</div>`)
    .join('');
}

/**
 * Trả về chuỗi hiển thị dung lượng lưu trữ (VD: "20GB/1CH/60 ngày")
 * @param {Object} plan - Đối tượng activeSubscriptionPlan
 * @param {Function} getDefaultRetention - Hàm lấy retention mặc định
 * @returns {string} Chuỗi định dạng
 */
export function buildCustomerPackageRetention(plan) {
  if (!plan || !plan.storage) return "";

  const { storage } = plan;

  const retention = storage.retention ?? getDefaultStorageRetention();
  const isConcurrent = (storage.type ?? StorageType.Fixed) === StorageType.ConcurrentMeeting;

  const strType = isConcurrent ? "/1CH" : "";
  const strDay = "ngày";
  const unitStorage = "GB";
  const size = bytesToGB(storage.maxSize);

  return `${size}${unitStorage}${strType}/${retention} ${strDay}`;
}


export function getStorageLabel(plan, storageText, meetingText, daysText, totalSize = 0) {
  if (!plan?.storage) return;

  const { type, maxSize, retention } = plan.storage || {};
  const sizeGB = bytesToGB(totalSize ?? 0, true);

  // Lấy text dịch, nếu không có thì để trống
  const labelParts = [];

  // Phần chính: storage
  if (sizeGB) {
    labelParts.push(`${storageText}: ${sizeGB}GB`);
  }

  // Nếu là SESSION thì thêm thông tin chi tiết
  if (type === StorageType.Session) {
    if (meetingText) labelParts.push(meetingText);
    if (retention) labelParts.push(`${retention} ${daysText}`);
  } else {

    if (retention) labelParts.push(`${retention} ${daysText}`);
  }

  // Nếu không có phần nào hợp lệ → trả về rỗng
  if (labelParts.length === 0) return "";

  // Ghép các phần lại với " / "
  return `| ${labelParts.join(" / ")}`;
}

export function getQuotaAILabel(
  plan: any,
  aiText = "AI",
  minutesText = "phút",
  unlimitedText = "Không giới hạn",
  noResetText = "Không reset",
  fixedText = "Cố định",
  noUse = "Không sử dụng",
  purchasedQuota = 0
) {
  if (!plan?.aiQuota) return "";

  const { type, minutes } = plan.aiQuota;

  if (type === AiQuotaType.Unlimited) {
    return `| ${aiText}: ${unlimitedText}`;
  }

  if (type === AiQuotaType.NoUse || type === null || type === undefined) {
    return `| ${aiText}: ${noUse}`;
  }

  if (type === AiQuotaType.Fixed || type === AiQuotaType.NoReset) {
    const totalMinutes = minutes + purchasedQuota;
    return `| ${aiText}: ${totalMinutes ?? 0} ${minutesText}`;
  }

  return "";
}

export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

///
export function getRemainingDaysOfPackage(endTime: string): number {
  try {
    const now = new Date();
    const end = new Date(endTime);

    const diffMs = end.getTime() - now.getTime();

    // Nếu đã hết hạn
    // if (diffMs <= 0) return diffMs;

    // Chuyển ms → ngày (lấy ceil để làm tròn lên)
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  } catch (error) {
    return 0;
  }
}

/**
 * Lấy thông tin thiết bị và trình duyệt để làm client address
 * Format: hđh_trìnhduyệt_loại thiết bị_version_timestamp
 */
export async function getClientAddress(): Promise<string> {
  const ua = navigator.userAgent;
  let os = "unknown_os";
  let browser = "unknown_browser";
  let browserVersion = "unknown_version";
  let deviceType = "desktop";

  // OS detection
  if (ua.indexOf("Win") !== -1) os = "windows";
  else if (ua.indexOf("Mac") !== -1) os = "macos";
  else if (ua.indexOf("X11") !== -1 || ua.indexOf("Linux") !== -1) os = "linux";
  else if (ua.indexOf("Android") !== -1) os = "android";
  else if (ua.indexOf("like Mac") !== -1) os = "ios";

  // 1. Try to get high entropy values if available (Chrome/Edge/Opera modern)
  const navAny = navigator as any;
  if (navAny.userAgentData && navAny.userAgentData.getHighEntropyValues) {
    try {
      const data = await navAny.userAgentData.getHighEntropyValues(['fullVersionList']);
      if (data.fullVersionList && data.fullVersionList.length > 0) {
        // Find the brand that matches a real browser, excluding "Not A;Brand"
        const brand = data.fullVersionList.find((b: any) =>
          /chrome|google chrome|edge|microsoft edge|opera/i.test(b.brand) &&
          !/not.a.brand/i.test(b.brand)
        );
        if (brand) {
          browserVersion = brand.version;
          if (/chrome|google chrome/i.test(brand.brand)) browser = "chrome";
          else if (/edge|microsoft edge/i.test(brand.brand)) browser = "edge";
          else if (/opera/i.test(brand.brand)) browser = "opera";
        }
      }
    } catch (e) {
      // Fallback
    }
  }

  // 2. Legacy Browser & Version detection (Fallback for Firefox/Safari/Older Chrome)
  if (browser === "unknown_browser" || browserVersion === "unknown_version" || browserVersion.endsWith(".0.0")) {
    if (ua.indexOf("Firefox") !== -1) {
      browser = "firefox";
      const match = ua.match(/Firefox\/([0-9.]+)/i);
      if (match) browserVersion = match[1];
    } else if (ua.indexOf("Opera") !== -1 || ua.indexOf("OPR") !== -1) {
      browser = "opera";
      const match = ua.match(/(?:Opera|OPR)\/([0-9.]+)/i);
      if (match) browserVersion = match[1];
    } else if (ua.indexOf("Trident") !== -1 || ua.indexOf("MSIE") !== -1) {
      browser = "ie";
      const match = ua.match(/(?:MSIE |rv:)([0-9.]+)/i);
      if (match) browserVersion = match[1];
    } else if (ua.indexOf("Edge") !== -1 || ua.indexOf("Edg") !== -1) {
      browser = "edge";
      const match = ua.match(/(?:Edge|Edg)\/([0-9.]+)/i);
      if (match) browserVersion = match[1];
    } else if (ua.indexOf("Chrome") !== -1) {
      browser = "chrome";
      const match = ua.match(/Chrome\/([0-9.]+)/i);
      if (match) browserVersion = match[1];
    } else if (ua.indexOf("Safari") !== -1) {
      browser = "safari";
      const match = ua.match(/Version\/([0-9.]+)/i);
      if (match) browserVersion = match[1];
    }
  }

  // 3. Device type
  if (/mobi|android|iphone|ipad|tablet/i.test(ua)) {
    deviceType = "mobile";
  }

  const timestamp = Math.floor(Date.now() / 1000);

  return `${os}_${browser}_${deviceType}_${browserVersion}_${timestamp}`.toLowerCase();
}

///
export function isCustomerPackageValid(endTime: string): boolean {
  try {
    return getRemainingDaysOfPackage(endTime) > 0;
  } catch (error) {
    return false;
  }
}

///
export function absValue(value) {
  try {
    return Math.abs(value);
  } catch (error) {
    return value;
  }
}

// /        {{ convertToGB(data.item.recordingSize) }}
export function getLabelForRecord(dataItem, yes?: string, no?: string) {
  if (dataItem.isRecorded === 1) {
    const size = smartBytesToUnit(dataItem.recordingSize);
    return yes + `\n ${size}`;
  }
  else if (dataItem.isRecorded === 0) {
    return no;
  }
  else {
    return no;
  }
}

export function getLabelForSum(dataItem, yes?: string, no?: string) {
  if (dataItem.usedSummary === 1) {
    const size = formatMinutesToHhMmSS(dataItem.summaryDuration);
    return yes + `\n ${size}`;
  }
  else if (dataItem.usedSummary === 0) {
    return no;
  }
  else {
    return no;
  }
}

export function formatDateRange(startDate, endDate) {
  return {
    fromDate: moment(startDate).format('YYYY-MM-DD[T]00:00:00[Z]'),
    toDate: moment(endDate).add(1, 'days').format('YYYY-MM-DD[T]00:00:00[Z]')
  };
}

export function formatDateRangeWithoutZ(dateRange) {

  if (!dateRange) return null;

  const m = moment(dateRange);

  if (!m.isValid()) {
    console.error("Dữ liệu ngày tháng không hợp lệ:", dateRange);
    return null;
  }

  // 3. Trả về định dạng: 2025-11-30T17-00-00
  return m.format('YYYY-MM-DD[T]HH-mm-ss');
}

export function jsonToExcelFile(
  data: any[],
  fileName = "import.xlsx"
): File {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  const wbout = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array"
  });

  return new File(
    [wbout],
    fileName,
    { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
  );
}

// Hàm xử lý ghi chú của excel
// export function normalizeHeader(raw: any): string {
//   return String(raw || "")
//     .normalize("NFC")
//     .replace(/\u00A0/g, " ")
//     .toUpperCase()
//     .split("\n")[0]
//     .split("(")[0]
//     .split(",")[0]
//     .replace(/[-–—/:]/g, " ")
//     .replace(/\s+/g, " ")
//     .trim();
// }

export function normalizeHeader(raw: any): string {
  return String(raw || "")
    .toUpperCase()
    .split(/\r?\n/)[0]   // 👈 LẤY DÒNG ĐẦU TIÊN
    .replace(/[-–—/:]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Tải file ghi âm/dữ liệu dung lượng lớn không gây RAM overhead.
 *   1. showSaveFilePicker (Chrome/Edge): Mở dialog Save As, stream trực tiếp xuống đĩa, hỗ trợ đổi tên tùy ý
 *   2. fallbackOpenFn / window.open: Mở tab mới để trình duyệt tự xử lý (Safari, Firefox, URL cross-origin)
 */
export async function downloadFile(url: string, filename: string, fallbackOpenFn?: (url: string) => void) {
  if (!url) return;

  if ('showSaveFilePicker' in window) {
    let handle: any = null;
    let writable: any = null;
    try {
      const ext = filename?.split('.').pop() || 'mp4';
      const mimeMap: Record<string, string> = { mp3: 'audio/mpeg', mp4: 'video/mp4' };

      handle = await (window as any).showSaveFilePicker({
        suggestedName: filename || 'recording',
        types: [{
          description: ext.toUpperCase(),
          accept: { [mimeMap[ext] || 'application/octet-stream']: [`.${ext}`] },
        }],
      });

      writable = await handle.createWritable();

      const response = await fetch(url);
      if (!response.ok || !response.body) {
        throw new Error(`Fetch failed: HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      let bytesWritten = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value && value.byteLength > 0) {
          await writable.write(value);
          bytesWritten += value.byteLength;
        }
      }

      if (bytesWritten === 0) {
        await writable.abort();
        throw new Error('Downloaded 0 bytes');
      }

      await writable.close();
      return;
    } catch (e: any) {
      if (e.name === 'AbortError') return;

      if (writable) {
        try { await writable.abort(); } catch { }
      }

      console.warn('showSaveFilePicker failed, falling back:', e.message);
    }
  }

  // Fallback
  if (fallbackOpenFn) {
    fallbackOpenFn(url);
  } else {
    window.open(url, '_blank');
  }
}
// export async function downloadFile(url: string, filename: string, fallbackOpenFn?: (url: string) => void) {
//   if (!url) return;

//   // File System Access API — stream trực tiếp, hỗ trợ đổi tên kể cả link cross-origin
//   if ('showSaveFilePicker' in window) {
//     try {
//       const ext = filename?.split('.').pop() || 'mp4';
//       const mimeMap: Record<string, string> = { mp3: 'audio/mpeg', mp4: 'video/mp4' };
//       const handle = await (window as any).showSaveFilePicker({
//         suggestedName: filename || 'recording',
//         types: [{
//           description: ext.toUpperCase(),
//           accept: { [mimeMap[ext] || 'application/octet-stream']: [`.${ext}`] },
//         }],
//       });
//       const writable = await handle.createWritable();
//       try {
//         const response = await fetch(url);
//         // Nếu server trả lỗi (CORS, auth, expired link...) → abort để tránh tạo file 0 bytes trên đĩa
//         if (!response.ok || !response.body) {
//           await writable.abort();
//           throw new Error(`Fetch failed: HTTP ${response.status}`);
//         }
//         await response.body.pipeTo(writable); // pipeTo tự đóng writable khi xong
//       } catch (fetchErr: any) {
//         // Cố gắng huỷ writable nếu chưa đóng (pipeTo chưa chạy hoặc bị lỗi giữa chừng)
//         try { await writable.abort(); } catch { /* ignore nếu đã closed */ }
//         throw fetchErr; // chuyển lên catch bên ngoài để fall through native download
//       }
//       return;
//     } catch (e: any) {
//       if (e.name === 'AbortError') return; // Người dùng huỷ dialog Save As
//       console.warn('showSaveFilePicker failed, falling back to native download:', e.message);
//     }
//   }

//   // Fallback
//   if (fallbackOpenFn) {
//     fallbackOpenFn(url);
//   } else {
//     window.open(url, "_blank");
//   }
// }

// ─────────────────────────────────────────────────────────────────────────────
// Shared DOC builder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Kiểu một section nội dung trong tài liệu xuất Word.
 *
 * - `type: 'heading'`   → thẻ <h2> tiêu đề mục
 * - `type: 'html'`      → nội dung HTML thuần, chèn trực tiếp
 * - `type: 'markdown'`  → chuỗi Markdown, tự động chuyển đổi sang HTML
 * - `type: 'timeline'`  → danh sách [{from, to, content}], hiển thị dạng timeline
 * - `type: 'userlist'`  → danh sách [{name, summary}], mỗi người một khối
 */
export type DocSection =
  | { type: 'heading'; text: string }
  | { type: 'html'; html: string }
  | { type: 'markdown'; content: string }
  | { type: 'timeline'; items: { from: number; to: number; content: string }[] }
  | { type: 'userlist'; items: { name: string; summary: string }[] };

export interface BuildMeetingDocOptions {
  /** Tiêu đề tài liệu (hiển thị giữa trang, in hoa) */
  title: string;
  /** Đối tượng cuộc họp — nếu truyền thì in phần "I. Thông tin cuộc họp" */
  meeting?: any;
  /** Danh sách các section nội dung theo thứ tự */
  sections?: DocSection[];
}

/**
 * Xây dựng chuỗi HTML hoàn chỉnh cho file tài liệu Word (.doc).
 * Hàm chung để tái sử dụng ở nhiều nơi, không phụ thuộc vào việc lưu file.
 *
 * @example
 * // Tóm tắt cuộc họp
 * const html = buildMeetingDocHtml({
 *   title: 'BIÊN BẢN CUỘC HỌP',
 *   meeting,
 *   sections: [
 *     { type: 'heading', text: 'II. Nội dung' },
 *     { type: 'markdown', content: summaryText },
 *   ],
 * });
 * await exportDocxFile('TomTat', html);
 */
export function buildMeetingDocHtml(options: BuildMeetingDocOptions): string {
  const { title, meeting, sections = [] } = options;

  // 1. Tiêu đề tài liệu
  let html = getHeadingMeetingContentExport(title);

  // 2. Thông tin cuộc họp (tuỳ chọn)
  if (meeting) {
    html += getMeetingOverviewDownloadHtml(meeting);
  }

  // 3. Các section nội dung
  for (const section of sections) {
    switch (section.type) {
      case 'heading':
        html += `
    <div style="margin-bottom: 12pt; font-family: 'Times New Roman'">
      <h2 style="font-size: 14pt; color: #000000; font-weight: bold; margin-top: 0; margin-bottom: 6pt; padding-bottom: 3px;">${section.text}</h2>
    </div>`;
        break;

      case 'html':
        html += section.html;
        break;

      case 'markdown': {
        const translated = convertTitleSummaryMeeting(section.content, true);
        html += `<div>${markdownToHtml(translated)}</div>`;
        break;
      }

      case 'timeline':
        for (const item of section.items) {
          const timeLine = convertTimeRange(item.from, item.to);
          const parsed = markdownToHtml(item.content);
          html += `
    <div style="margin-bottom: 16pt;">
      <div style="font-weight: bold; color: #000000; font-size: 14pt; margin-bottom: 4pt;">${timeLine}</div>
      <div>${parsed}</div>
    </div>`;
        }
        break;

      case 'userlist':
        for (const item of section.items) {
          const parsed = markdownToHtml(item.summary);
          html += `
    <div style="margin-bottom: 20pt; padding-bottom: 8pt;">
      <h2 style="color: #000000; border: none; margin-top: 0; font-size: 14pt; font-weight: bold;">${item.name}</h2>
      <div>${parsed}</div>
    </div>`;
        }
        break;
    }
  }

  return html;
}

/**
 * Tạo HTML tiêu đề tài liệu xuất Word (căn giữa, in đậm).
 * @param title - Tiêu đề tài liệu hiển thị giữa trang
 */
export function getHeadingMeetingContentExport(title: string) {
  return `
    <div style="text-align: center; margin-bottom: 18pt; font-family: 'Times New Roman'">
      <h1 style="font-size: 16pt; color: #000000; font-weight: bold; margin-top: 0; margin-bottom: 6pt;">${title}</h1>
      <p style="font-style: italic; color: #555555; font-size: 10.5pt; margin-top: 0; margin-bottom: 0;">Nội dung được tổng hợp từ AI có thể chưa đầy đủ hoặc có sai sót. Vui lòng kiểm tra lại nội dung phiên họp để đảm bảo tính chính xác!</p>
    </div>
  `;
}

/**
 * Tạo mã HTML cho phần Thông tin cuộc họp (dạng danh sách bullet) chèn vào đầu các file khi download/export.
 */
export function getMeetingOverviewDownloadHtml(meeting: any): string {
  if (!meeting) return "";

  // Ngày họp
  const meetingDate = localDateFormat(meeting.actualStartTime) || "--";

  // Thời gian thực tế
  let actualTimeStr = "-";
  if (meeting.actualStartTime) {
    actualTimeStr = localTimeFormat(meeting.actualStartTime);
    if (meeting.actualEndTime) {
      if (localDateFormat(meeting.actualStartTime) === localDateFormat(meeting.actualEndTime)) {
        actualTimeStr += " - " + localTimeFormat(meeting.actualEndTime);
      } else {
        actualTimeStr = `${localTimeFormat(meeting.actualStartTime)} ngày ${localDateFormat(meeting.actualStartTime)} - ${localTimeFormat(meeting.actualEndTime)} ngày ${localDateFormat(meeting.actualEndTime)}`;
      }
    }
  }

  /// Số người tham dự thực tế
  const numUsers = meeting.numUsers || '--';

  return `
    <div style="margin-bottom: 12pt; font-family: 'Times New Roman';">
      <h2 style="font-size: 14pt; color: #000000; font-weight: bold; margin-top: 0; margin-bottom: 6pt; padding-bottom: 3px;">I. Thông tin</h2>
      <table cellpadding="0" cellspacing="0" style="margin-left: 24pt; width: 100%; font-size: 12pt; color: #000000; border-collapse: collapse; line-height: 1.25;">
        <tr>
          <td style="width: 120pt; font-weight: bold; padding: 2pt 0; color: #000000; vertical-align: top;">- ID:</td>
          <td style="padding: 2pt 0; color: #000000; vertical-align: top;">${meeting.id || ''}</td>
        </tr>
        <tr>
          <td style="width: 120pt; font-weight: bold; padding: 2pt 0; color: #000000; vertical-align: top;">- Phiên họp:</td>
          <td style="padding: 2pt 0; color: #000000; vertical-align: top;">${meeting.name || ''}</td>
        </tr>
        <tr>
          <td style="font-weight: bold; padding: 2pt 0; color: #000000; vertical-align: top;">- Ngày:</td>
          <td style="padding: 2pt 0; color: #000000; vertical-align: top;">${meetingDate}</td>
        </tr>
        <tr>
          <td style="font-weight: bold; padding: 2pt 0; color: #000000; vertical-align: top;">- Thời gian:</td>
          <td style="padding: 2pt 0; color: #000000; vertical-align: top;">${actualTimeStr}</td>
        </tr>
        <tr>
          <td style="font-weight: bold; padding: 2pt 0; color: #000000; vertical-align: top;">- Số người tham dự:</td>
          <td style="padding: 2pt 0; color: #000000; vertical-align: top;">${numUsers}</td>
        </tr>
      </table>
      <div style="margin-bottom: 6pt;"></div>
    </div>
  `;
}

/**
 * Đánh lại số thứ tự cho các tiêu đề ## trong markdown, bắt đầu từ startIndex
 */
export function renumberMarkdownHeadings(md: string, startIndex: number = 2): string {
  if (!md) return "";

  // Normalize newlines
  let content = md.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  // Xóa tiêu đề H1 (ví dụ # Biên bản cuộc họp)
  content = content.replace(/^#\s+.*$/m, "");

  const lines = content.split("\n");
  let headingIndex = startIndex;

  const processedLines = lines.map(line => {
    const trimmed = line.trim();
    if (trimmed.startsWith("## ")) {
      const title = trimmed.substring(3).trim();
      // Loại bỏ số thứ tự cũ ở đầu tiêu đề (nếu có, ví dụ "1. ", "A. ")
      const cleanTitle = title.replace(/^\d+\.\s*/, "");
      const newLine = `## ${headingIndex}. ${cleanTitle}`;
      headingIndex++;
      return newLine;
    }
    return line;
  });

  return processedLines.join("\n");
}

/**
 * Lấy dung lượng trống và loại gói cước
 */
export function freeStorage(customer): { remainingMB: string; isEnough: boolean } {
  const maxSize = customer?.maxStorageSizeAfterPurchase ?? customer?.maxStorageSize ?? 0;

  /// Quan trọng: Nếu maxSize là 0 (hệ thống chưa tính dung lượng của KH)
  /// => trả isEnough về true để không hiển thị cảnh báo dung lượng
  if (maxSize === 0) {
    return { remainingMB: '0MB', isEnough: true };
  }

  // Sử dụng Math.max để tránh hiển thị dung lượng âm
  const remaining = Math.max(0, customer?.remainingStorageSize ?? 0);

  const remainingMB = bytesToMB(remaining, true, 2) + "MB";
  const isEnough = !customer?.isStorageLowForRecording;

  return { remainingMB, isEnough };
}

/**
 * Hiển thị dialog xác nhận khi không đủ dung lượng để bắt đầu cuộc họp có ghi hình
 */
export async function confirmStartMeetingWithoutRecording(
  vueInstance: any,
  customer: any,
  onConfirm: () => Promise<void> | void,
  onOpenStorage: () => void
): Promise<void> {
  const h = vueInstance.$createElement;
  const freeStorageInfo = freeStorage(customer);

  const modalMessage = h('section', {
    style: {
      backgroundColor: '#fff5f5',
      padding: '20px',
      borderRadius: '12px',
      border: '1px solid #ffe3e3',
      borderLeft: '5px solid #e53e3e',
      boxShadow: '0 4px 12px rgba(229, 62, 62, 0.04)',
      fontFamily: 'inherit'
    }
  }, [
    h(
      'div',
      {
        style: {
          display: 'flex',
          alignItems: 'center',
          color: '#9b2c2c',
          fontSize: '17px',
          fontWeight: '700',
          marginBottom: '10px'
        }
      },
      [
        vueInstance.$t('notEnoughStorageToRecord').toString()
      ]
    ),

    h('div', {
      style: {
        fontSize: '14.5px',
        color: '#5c6470',
        lineHeight: '1.6',
        marginBottom: '8px'
      }
    }, [
      h(
        'span',
        vueInstance.$t('recordingStorageRequirement', {
          storage: freeStorageInfo.remainingMB
        }).toString()
      ),
      ' ',
      h('b', { style: { color: '#2c3e50' } }, vueInstance.$t('meetingNotRecordable').toString()),
      '.'
    ]),

    h('div', {
      style: {
        fontSize: '14px',
        color: '#5c6470'
      }
    }, [
      vueInstance.$t('storageCleanupPrompt').toString(),
      ' ',
      h(
        'a',
        {
          attrs: { href: 'javascript:void(0)' },
          style: {
            color: '#004f9e',
            fontWeight: '600',
            textDecoration: 'underline'
          },
          on: {
            click: (e: Event) => {
              e.preventDefault();
              onOpenStorage();
            }
          }
        },
        [h('strong', vueInstance.$t('here').toString())]
      )
    ])
  ]);

  try {
    const confirm = await vueInstance.$bvModal.msgBoxConfirm([modalMessage], {
      title: vueInstance.$t('alert').toString(),
      size: 'lg',
      okVariant: 'primary',
      okTitle: vueInstance.$t('startMeetingWithoutRecording').toString(),
      cancelTitle: vueInstance.$t('cancel').toString(),
      centered: true,
      returnFocus: document.body
    });
    if (confirm) {
      await onConfirm();
    }
  } catch (err) {
    console.error(err);
  } finally {
    (document.activeElement as HTMLElement)?.blur();
  }
}



